import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { bold, medium, mediumInter, heavy, colors } from '@/styles';

function getProgressHeight(size) {
  switch (size) {
    case 'xsmall':
      return '0.3125rem';
    case 'small':
      return '0.875rem';
    case 'large':
      return '1.125rem';
    default:
      return '1rem';
  }
}

/**
 * ProgressBar component
 *
 * @param {import('./ProgressBar').ProgressBarProps} props
 */
function ProgressBar({
  className,
  maxValue,
  backgroundColorName,
  backgroundColor,
  currentValue,
  currentColorName,
  currentColor,
  highlightValue,
  highlightColorName,
  highlightColor,
  textPlacement,
  textContent,
  size,
  font,
}) {
  const [hover, toggleHover] = useState(false);

  const handleHover = useCallback(
    (show) => {
      if (textPlacement !== 'tooltip-hover') {
        return;
      }
      toggleHover(show);
    },
    [textPlacement],
  );

  const highlightValueClass =
    highlightValue && currentValue >= highlightValue ? 'highlight-value' : null;

  const percent = currentValue / maxValue;
  const progressWidth = `max(${percent * 100}%, ${getProgressHeight(size)})`;
  const tooltipLeft = `calc(${(percent / 2) * 100}% - ${textContent.length * 3.5}px - 1rem)`;

  return (
    <div className={className}>
      {textPlacement === 'over' && textContent !== '' && (
        <div className="text-over">
          <span>{textContent}</span>
          <span className={highlightValueClass}>{`${currentValue}%`}</span>
        </div>
      )}

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="bar-container"
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
      >
        {textPlacement === 'around' && textContent !== '' && <span>{textContent}</span>}

        <div className="progress-background">
          <div
            className="progress"
            style={{ width: progressWidth }}
            role="progressbar"
            aria-label={textContent || 'Progress'}
            aria-valuemin={0}
            aria-valuemax={maxValue}
            aria-valuenow={currentValue}
          />
        </div>
        {textPlacement === 'around' && textContent !== '' && (
          <span className={highlightValueClass}>{`${currentValue}%`}</span>
        )}
      </div>

      {(textPlacement === 'tooltip' || hover) && (
        <div className="tooltip-container">
          <div role="tooltip" className="tooltip" style={{ left: tooltipLeft }}>
            <span>{textContent}</span>
          </div>
        </div>
      )}

      {textPlacement === 'below' && textContent !== '' && (
        <div className="text-over">
          <span>{textContent}</span>
          <span className={highlightValueClass}>{`${currentValue}%`}</span>
        </div>
      )}
    </div>
  );
}

ProgressBar.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** The color to use for the background of the progress bar */
  backgroundColorName: PropTypes.string,
  backgroundColor: PropTypes.string,
  /** The maximum value */
  maxValue: PropTypes.number.isRequired,
  /** The current value */
  currentValue: PropTypes.number.isRequired,
  /** The color to use for the "current" value (not highlight) */
  currentColorName: PropTypes.string,
  currentColor: PropTypes.string,
  /** Value used to color printed value when currentValue is at or above this value. */
  highlightValue: PropTypes.number,
  /** Name of the color value to use when highlightValue is active. */
  highlightColorName: PropTypes.string,
  highlightColor: PropTypes.string,
  /** How text should be displayed on the progress bar */
  textPlacement: PropTypes.oneOf(['tooltip', 'tooltip-hover', 'over', 'around', 'below']),
  /** A label for the progress bar */
  textContent: PropTypes.string,
  /** What size should the progress bar be */
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
  /** Which font should be used */
  font: PropTypes.oneOf(['avenir', 'inter']),
};

ProgressBar.defaultProps = {
  backgroundColorName: null,
  backgroundColor: colors.silverSand,
  currentColorName: null,
  currentColor: colors.taosSky,
  highlightValue: null,
  highlightColorName: null,
  highlightColor: null,
  textPlacement: 'over',
  textContent: '',
  size: 'medium',
  font: 'avenir',
};

const styledProgressBar = styled(ProgressBar)`
  ${(props) => {
    switch (props.size) {
      case 'xsmall':
        return 'font-size: 0.75rem;';
      case 'small':
        return 'font-size: 0.875rem;';
      case 'large':
        return 'font-size: 1.125rem;';
      default:
        return 'font-size: 1rem;';
    }
  }}
  ${(props) => {
    switch (props.size) {
      case 'xsmall':
        return props.font === 'avenir' ? heavy() : bold();
      default:
        return props.font === 'avenir' ? medium() : mediumInter();
    }
  }}
  color: ${colors.battleship};
  width: 100%;

  .text-over {
    display: flex;
    justify-content: space-between;
    ${(props) => {
      const placement = props.textPlacement === 'below' ? 'top' : 'bottom';
      return `margin-${placement}: 0.2rem;`;
    }}
  }

  ${(props) => {
    if (props.highlightColor) {
      return `.highlight-value { color: ${colors[props.highlightColor]}; }`;
    } else if (props.highlightColorName) {
      return `.highlight-value { color: ${colors[props.highlightColorName]}; }`;
    }

    return '';
  }}

  .bar-container {
    ${(props) => {
      if (props.textPlacement === 'around') {
        return `
        display: flex;

        .progress-background {
          flex: 1;
          margin: 0.125rem 1rem 0;
        }
        `;
      }
      return '';
    }}
  }

  .progress-background {
    ${(props) => `height: ${getProgressHeight(props.size)};`}
    background: ${(props) => props.backgroundColor ?? props.backgroundColorName};
    border-radius: 1rem;

    .progress {
      height: 100%;
      background: ${(props) => props.currentColor ?? props.currentColorName};
      border-radius: 1rem;
      transition: width 1s;
      transition-timing-function: ease-in-out;
    }
  }

  .tooltip-container {
    position: relative;

    .tooltip {
      border-radius: 0.25rem;
      padding: 0.8rem;
      background: ${colors.fog};
      box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadow};
      position: absolute;
      top: -4.35rem;
      transition: left 1s;
      transition-timing-function: ease-in-out;

      &::after {
        content: ' ';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -0.5rem;
        border-width: 0.5rem;
        border-style: solid;
        border-color: ${colors.fog} transparent transparent transparent;
      }

      span {
        ${(props) => (props.font === 'avenir' ? heavy() : bold())}
        font-size: 0.875rem;
        color: ${colors.silverSandAlt};
      }
    }
  }
`;

export default styledProgressBar;
